import React, { useEffect, useState } from 'react';
import { UserCircle, Save, CalendarCheck, BellRing } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useSession } from '../lib/SessionContext';
import { t } from '../i18n';
import { Card, Button, Input, Field, Alert, Spinner } from '../components/ui';

/**
 * A patient's own settings: contact details, and the two emails around a
 * session. Professionals set theirs in Configuración, where /mi-perfil sends
 * them (App.js).
 *
 * Email is not editable here because it identifies the account and is what
 * claims and password resets match on; changing it belongs in a flow that
 * verifies the new address.
 */

const ProfileView = () => {
  const { profile, refresh } = useSession();

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (!profile) return;
    setForm({
      first_name: profile.first_name ?? '',
      last_name: profile.last_name ?? '',
      phone: profile.phone ?? '',
      wants_calendar_invites: profile.wants_calendar_invites ?? true,
      wants_reminders: profile.wants_reminders ?? true,
    });
  }, [profile]);

  const save = async () => {
    setSaving(true);
    setError(null);
    const { error: saveError } = await supabase.from('accounts').update({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      phone: form.phone.trim() || null,
      wants_calendar_invites: form.wants_calendar_invites,
      wants_reminders: form.wants_reminders,
    }).eq('id', profile.id);
    setSaving(false);
    if (saveError) { setError(saveError.message); return; }
    setNotice(t('profile.saved'));
    refresh();
  };

  if (!form) return <Spinner />;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
          <UserCircle size={24} className="text-slate-400" />{t('profile.title')}
        </h1>
        <p className="text-sm text-slate-500">{profile.email}</p>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      <Card title={t('profile.contact')}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('register.firstName')} required>
            <Input value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </Field>
          <Field label={t('register.lastName')} required>
            <Input value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </Field>
          <Field label={t('register.phone')}>
            <Input type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Field>
          <Field label={t('auth.email')} hint={t('profile.emailFixed')}>
            <Input value={profile.email ?? ''} disabled />
          </Field>
        </div>
      </Card>

      {/* Separate on purpose: somebody can want the session in their calendar
          and no email about it, or the opposite. */}
      <Card title={t('profile.notifications')}>
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1"
            checked={form.wants_calendar_invites}
            onChange={(e) => setForm({ ...form, wants_calendar_invites: e.target.checked })}
          />
          <span>
            <span className="flex items-center gap-1.5 font-medium text-slate-800">
              <CalendarCheck size={15} />{t('profile.calendarInvites')}
            </span>
            <span className="mt-0.5 block text-sm text-slate-500">
              {t('profile.calendarInvitesHint')}
            </span>
          </span>
        </label>

        <label className="mt-4 flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1"
            checked={form.wants_reminders}
            onChange={(e) => setForm({ ...form, wants_reminders: e.target.checked })}
          />
          <span>
            <span className="flex items-center gap-1.5 font-medium text-slate-800">
              <BellRing size={15} />{t('profile.reminders')}
            </span>
            <span className="mt-0.5 block text-sm text-slate-500">
              {t('profile.remindersHint')}
            </span>
          </span>
        </label>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} loading={saving}><Save size={16} />{t('common.save')}</Button>
      </div>
    </div>
  );
};

export default ProfileView;
